import { getSettingValueSelector } from "@client/selectors";
import { type GenericMap } from "@client/types";
import { type StyleSheetTextType } from "@client/types/StyleSheet";
import {
  getStyleSheetClassName,
  getStyleSheetColorClassName,
  getStyleSheetLinkClassName
} from "@client/widgets/Containers/HTML/Assets/StyleSheet/lib";
import { isColorLike } from "@common/lib/color";
import { joinDefined } from "@common/lib/misc";
import { type Property } from "csstype";
import { createSelector } from "reselect";
import { propsStyleSelector } from "../selectors";

export const ALIGN_TO_JUSTIFY_CONTENT: GenericMap<Property.JustifyContent> = {
  center: "center",
  justify: "stretch",
  left: "flex-start",
  right: "flex-end"
};
export const makeStyleSelector = (target: string) => {
  const textAlignSelector = getSettingValueSelector(target, "alignText");

  return createSelector([textAlignSelector, propsStyleSelector], (textAlign, propsStyle): React.CSSProperties => {
    return {
      justifyContent: ALIGN_TO_JUSTIFY_CONTENT[textAlign ?? "left"],
      textAlign,
      ...propsStyle
    };
  });
};

export const makeStyleSheetTextClassNameSelector = (
  target: string,
  styleSheets: StyleSheetTextType[],
  prefix?: string
) => {
  const textClassNamesSelector = createSelector(
    styleSheets.map((x) => getSettingValueSelector(target, `s_text`, joinDefined(".", prefix, x))),
    (...args) => args.map((x, i) => (x ? getStyleSheetClassName(styleSheets[i], x) : undefined))
  );
  const colorClassNamesSelector = createSelector(
    styleSheets.map((x) => getSettingValueSelector(target, `color`, joinDefined(".", prefix, x))),
    (...args) => args.map((x, i) => (x && !isColorLike(x) ? getStyleSheetColorClassName(styleSheets[i], x) : undefined))
  );
  const linkClassNamesSelector = createSelector(
    styleSheets.map((x) => getSettingValueSelector(target, `s_link`, joinDefined(".", prefix, x))),
    (...args) => args.map((x, i) => (x ? getStyleSheetLinkClassName(styleSheets[i], x) : undefined))
  );

  return createSelector(
    [textClassNamesSelector, colorClassNamesSelector, linkClassNamesSelector],
    (textClasses, colorClasses, linkClasses) => joinDefined(" ", ...textClasses, ...colorClasses, ...linkClasses)
  );
};
